import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from "react";
import type { ChatMessage } from "../types";
import { DateDivider } from "./DateDivider";
import { MessageBubble } from "./MessageBubble";
import { SystemMessagePill } from "./SystemMessagePill";

interface MessageListProps {
  messages: ChatMessage[];
  currentUserId: string | undefined;
  privileged: boolean;
  highlightTerm: string;
  hasMore: boolean;
  isFetchingMore: boolean;
  onLoadMore: () => void;
  /** Anchored-history mode: newer messages exist below the loaded buffer. */
  hasNewer?: boolean;
  isFetchingNewer?: boolean;
  onLoadNewer?: () => void;
  /** When false (anchored-history mode) the list never auto-scrolls to the
   *  bottom — the caller controls the scroll position (jump-to-message). */
  pinToBottom?: boolean;
  onReact: (messageId: string, emoji: string) => void;
  onClearReaction: (messageId: string) => void;
  onReply: (message: ChatMessage) => void;
  onEdit: (messageId: string, body: string) => void;
  onDelete: (messageId: string) => void;
  onShowHistory: (messageId: string) => void;
  onJumpToReply: (id: string) => void;
  onOpenReactions: (message: ChatMessage, emoji?: string) => void;
  scrollKey: string;
}

export interface MessageListHandle {
  scrollToMessage: (id: string) => void;
  scrollToBottom: () => void;
  hasMessage: (id: string) => boolean;
}

/** Messages closer than this from the same sender read as one utterance. */
const GROUP_GAP_MS = 5 * 60 * 1000;

/** Does `m` continue the run started by `prev`? Same sender, same day, and no
 *  long pause — a system pill (sender=null) always breaks the run. */
function continuesRun(
  prev: ChatMessage | undefined,
  m: ChatMessage | undefined,
): boolean {
  if (!prev || !m || !prev.sender || !m.sender) return false;
  if (prev.sender.id !== m.sender.id) return false;
  if (!sameDay(prev, m)) return false;
  const dt =
    new Date(m.created_at).getTime() - new Date(prev.created_at).getTime();
  return Number.isFinite(dt) && dt < GROUP_GAP_MS;
}

function sameDay(a: ChatMessage, b: ChatMessage): boolean {
  try {
    return (
      new Date(a.created_at).toDateString() ===
      new Date(b.created_at).toDateString()
    );
  } catch {
    return true;
  }
}

export const MessageList = forwardRef<MessageListHandle, MessageListProps>(
  function MessageListInner(
    {
      messages,
      currentUserId,
      privileged,
      highlightTerm,
      hasMore,
      isFetchingMore,
      onLoadMore,
      hasNewer = false,
      isFetchingNewer = false,
      onLoadNewer,
      pinToBottom = true,
      onReact,
      onClearReaction,
      onReply,
      onEdit,
      onDelete,
      onShowHistory,
      onJumpToReply,
      onOpenReactions,
      scrollKey,
    },
    ref,
  ) {
    const scrollerRef = useRef<HTMLDivElement>(null);
    const bottomRef = useRef<HTMLDivElement>(null);
    const topSentinelRef = useRef<HTMLDivElement>(null);
    const bottomSentinelRef = useRef<HTMLDivElement>(null);
    const lastSeenIdRef = useRef<string | null>(null);
    const refsById = useRef(new Map<string, HTMLDivElement>());

    const registerRef = useCallback(
      (id: string, el: HTMLDivElement | null) => {
        if (el) refsById.current.set(id, el);
        else refsById.current.delete(id);
      },
      [],
    );

    useImperativeHandle(
      ref,
      () => ({
        scrollToMessage(id: string) {
          const el = refsById.current.get(id);
          if (!el) return;
          // Unpin BEFORE scrolling: while the smooth animation runs, a late
          // image decode would otherwise re-pin the scroller to the bottom
          // and swallow the jump.
          pinnedRef.current = false;
          userScrolledRef.current = true;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          el.classList.add("ring-2", "ring-red-300", "ring-offset-1");
          setTimeout(() => {
            el.classList.remove("ring-2", "ring-red-300", "ring-offset-1");
          }, 1500);
        },
        scrollToBottom() {
          const el = scrollerRef.current;
          if (!el) return;
          pinnedRef.current = true;
          userScrolledRef.current = false;
          el.scrollTop = el.scrollHeight;
          requestAnimationFrame(() => {
            const e2 = scrollerRef.current;
            if (e2) e2.scrollTop = e2.scrollHeight;
          });
        },
        hasMessage(id: string) {
          return refsById.current.has(id);
        },
      }),
      [],
    );

    // ── auto-pinning ────────────────────────────────────────────────────
    // `pinnedRef` tracks whether the scroller is "stuck to the bottom". It
    // stays true until the user scrolls up by themselves; any subsequent
    // size change (images decoding, link cards loading, new messages) keeps
    // the scroller pegged at scrollHeight until the user opts out.

    const pinnedRef = useRef(true);
    const userScrolledRef = useRef(false);
    // Anchored-history mode (pinToBottom=false): the bottom of the buffer is
    // NOT the newest message, so sticking to it would fight the jump scroll
    // and the newer-page loader. Mirrored into a ref so the long-lived
    // observers/effects below see the current value without re-subscribing.
    const pinAllowedRef = useRef(pinToBottom);
    pinAllowedRef.current = pinToBottom;
    useEffect(() => {
      if (!pinToBottom) pinnedRef.current = false;
    }, [pinToBottom]);
    // Captured right before a `fetchNextPage` so the post-fetch layout effect
    // can offset scrollTop by the height delta of the newly-prepended page
    // — otherwise the viewport jumps (either down to the new scrollHeight if
    // we accidentally re-pin, or up to the freshly-inserted content).
    const prevScrollHeightRef = useRef(0);
    // First message id + scroller height as of the previous layout pass.
    // Older pages can be prepended WITHOUT the top-sentinel snapshot above
    // (the jump-to-message loop calls fetchNextPage directly), so prepends
    // are detected structurally: same last id, different first id.
    const firstSeenIdRef = useRef<string | null>(null);
    const lastHeightRef = useRef(0);

    // Reset pin/anchor on conversation change.
    useEffect(() => {
      lastSeenIdRef.current = null;
      if (!pinAllowedRef.current) return;
      pinnedRef.current = true;
      userScrolledRef.current = false;
      const el = scrollerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }, [scrollKey]);

    // RAF-loop right after mount/room-switch — re-pins the scroller every
    // animation frame for ~1.5s. This catches the common race where the
    // initial scroll happens BEFORE messages arrive (height=0), then the
    // history fills in over a few frames and a single pin in useEffect lands
    // mid-list. We keep pinning until the user actually scrolls or the time
    // window expires.
    useEffect(() => {
      const el = scrollerRef.current;
      if (!el) return;
      let cancelled = false;
      const start = performance.now();
      const tick = () => {
        if (cancelled) return;
        if (pinnedRef.current) el.scrollTop = el.scrollHeight;
        if (performance.now() - start < 1500) {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);
      return () => {
        cancelled = true;
      };
    }, [scrollKey]);

    // Track scroll position. Pin is purely a function of distance from the
    // bottom — keyboard scroll, scrollbar drag and trackpad fling all flow
    // through `scroll`, while `wheel`/`touchmove` are unreliable as the sole
    // signal (e.g. they don't fire when the user drags the scrollbar).
    useEffect(() => {
      const el = scrollerRef.current;
      if (!el) return;
      function onScroll() {
        if (!el) return;
        const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
        pinnedRef.current = pinAllowedRef.current && dist < 80;
        if (pinnedRef.current) userScrolledRef.current = false;
      }
      el.addEventListener("scroll", onScroll, { passive: true });
      return () => {
        el.removeEventListener("scroll", onScroll);
      };
    }, []);

    // Resize observer pins the scroller whenever its content grows, for as
    // long as we're still "pinned". No time limit — images can decode many
    // seconds after the initial paint.
    useEffect(() => {
      const el = scrollerRef.current;
      if (!el || !("ResizeObserver" in window)) return;
      const pin = () => {
        if (pinnedRef.current) el.scrollTop = el.scrollHeight;
      };
      const ro = new ResizeObserver(pin);
      ro.observe(el);
      // Also observe nested descendants (the inner content wrapper, plus
      // anything dynamically added) so individual image inflation triggers a
      // re-pin even if the scroller's own size didn't change.
      const mo = new MutationObserver(() => {
        for (const child of Array.from(el.querySelectorAll("img, video, iframe"))) {
          ro.observe(child);
        }
        pin();
      });
      mo.observe(el, { childList: true, subtree: true });
      // Initial sweep over existing media.
      for (const child of Array.from(el.querySelectorAll("img, video, iframe"))) {
        ro.observe(child);
      }
      return () => {
        ro.disconnect();
        mo.disconnect();
      };
    }, []);

    // Run synchronously after DOM commit, BEFORE the browser paints — so the
    // user never sees the scroller stop mid-list while React still has the
    // chance to put it at the bottom.
    useLayoutEffect(() => {
      const last = messages[messages.length - 1];
      const first = messages[0];
      const prevId = lastSeenIdRef.current;
      const prevFirstId = firstSeenIdRef.current;
      lastSeenIdRef.current = last?.id ?? null;
      firstSeenIdRef.current = first?.id ?? null;
      const el = scrollerRef.current;
      if (!last || !el) return;

      // If we just prepended older history via fetchNextPage, keep the user
      // anchored to the same message they were looking at instead of letting
      // the scroller drift (and never re-pin them to the bottom). The
      // sentinel snapshot is the accurate height source when present; the
      // previous pass's height covers prepends the sentinel never saw
      // (jump-to-message keeps fetching pages while pinned to the bottom).
      const prepended =
        prevId !== null &&
        last.id === prevId &&
        prevFirstId !== null &&
        first.id !== prevFirstId;
      const prevH = prevScrollHeightRef.current || lastHeightRef.current;
      prevScrollHeightRef.current = 0;
      if (prepended && prevH > 0) {
        const delta = el.scrollHeight - prevH;
        if (delta > 0) el.scrollTop = el.scrollTop + delta;
        // Content grew ABOVE the viewport — whatever the pin state was, the
        // user must stay anchored where they were, not get dragged down.
        pinnedRef.current = false;
        lastHeightRef.current = el.scrollHeight;
        return;
      }

      const isFirstLoad = prevId === null;
      if ((isFirstLoad || pinnedRef.current) && pinAllowedRef.current) {
        pinnedRef.current = true;
        userScrolledRef.current = false;
        el.scrollTop = el.scrollHeight;
        // Re-pin one frame later — async media inflation often lands here.
        requestAnimationFrame(() => {
          if (pinnedRef.current) el.scrollTop = el.scrollHeight;
        });
      }
      lastHeightRef.current = el.scrollHeight;
    }, [messages]);

    useEffect(() => {
      const node = topSentinelRef.current;
      if (!node) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && hasMore && !isFetchingMore) {
              const el = scrollerRef.current;
              // Snapshot the scrollHeight just before requesting the next
              // page. The post-fetch effect uses this to keep the bubble
              // currently under the cursor exactly where it is.
              if (el) prevScrollHeightRef.current = el.scrollHeight;
              onLoadMore();
            }
          }
        },
        { root: scrollerRef.current, threshold: 0 },
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [hasMore, isFetchingMore, onLoadMore]);

    // Anchored-history mode: mirror sentinel at the BOTTOM loads the pages
    // NEWER than the buffer as the user scrolls down towards the present.
    // No scroll compensation needed — content grows below the viewport.
    useEffect(() => {
      const node = bottomSentinelRef.current;
      if (!node || !onLoadNewer) return;
      const observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting && hasNewer && !isFetchingNewer) {
              onLoadNewer();
            }
          }
        },
        { root: scrollerRef.current, threshold: 0 },
      );
      observer.observe(node);
      return () => observer.disconnect();
    }, [hasNewer, isFetchingNewer, onLoadNewer]);

    if (messages.length === 0) {
      return (
        <div className="flex flex-1 items-center justify-center bg-[#f2f2f2] text-sm text-gray-500">
          Brak wiadomości. Napisz pierwszą!
        </div>
      );
    }

    return (
      <div
        ref={scrollerRef}
        className="flex-1 overflow-y-auto bg-[#f2f2f2] px-2 py-3 sm:px-4"
      >
        <div ref={topSentinelRef} />
        {isFetchingMore && (
          <div className="text-center text-[11px] text-gray-400 py-1">
            Wczytuję starsze wiadomości…
          </div>
        )}
        {!hasMore && (
          <div className="text-center text-[11px] text-gray-400 py-1">
            — początek konwersacji —
          </div>
        )}
        {messages.map((m, idx) => {
          const prev = messages[idx - 1];
          const next = messages[idx + 1];
          const newDay = !prev || !sameDay(prev, m);
          // System messages (sender=null) render as centered pills, not bubbles.
          if (!m.sender) {
            return (
              <Fragment key={m.id}>
                {newDay && <DateDivider iso={m.created_at} />}
                <SystemMessagePill body={m.body} />
              </Fragment>
            );
          }
          // A run of messages from one person collapses into a single block:
          // one name at the top, one clock stamp at the bottom.
          const inRun = continuesRun(prev, m);
          const showSender = !inRun;
          const showTime = !continuesRun(m, next);
          const mine = m.sender?.id === currentUserId;
          return (
            <Fragment key={m.id}>
              {newDay && <DateDivider iso={m.created_at} />}
              <MessageBubble
                message={m}
                mine={mine}
                showSender={showSender}
                groupStart={!inRun}
                showTime={showTime}
                highlightTerm={highlightTerm}
                privileged={privileged}
                onReact={onReact}
                onClearReaction={onClearReaction}
                onReply={onReply}
                onEdit={onEdit}
                onDelete={onDelete}
                onShowHistory={onShowHistory}
                onJumpToReply={onJumpToReply}
                onOpenReactions={onOpenReactions}
                registerRef={registerRef}
              />
            </Fragment>
          );
        })}
        {isFetchingNewer && (
          <div className="text-center text-[11px] text-gray-400 py-1">
            Wczytuję nowsze wiadomości…
          </div>
        )}
        <div ref={bottomSentinelRef} />
        <div ref={bottomRef} />
      </div>
    );
  },
);
